import { initializeApp } from 'firebase/app'
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged,
  type User,
} from 'firebase/auth'
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  deleteDoc,
} from 'firebase/firestore'

const firebaseConfig = {
  apiKey: 'AIzaSyAjpYThNrNWDdcKp6N8mn1m4B52Ty__NnE',
  authDomain: 'shop-16968.firebaseapp.com',
  projectId: 'shop-16968',
  storageBucket: 'shop-16968.firebasestorage.app',
  messagingSenderId: '1057983305014',
  appId: '1:1057983305014:web:4e63c75557b7c47bcbcf97',
  measurementId: 'G-4M5D03LZMT',
}

const app = initializeApp(firebaseConfig)
export const auth = getAuth(app)
export const db = getFirestore(app)

// ─── Types ───────────────────────────────────────────────

export interface CustomerProfile {
  email: string
  displayName: string
  createdAt: string
  marketingOptIn?: boolean
}

export interface OrderLineItem {
  productId: string
  name: string
  quantity: number
  unitPrice: number // in cents
  type: 'book' | 'supplement'
}

export interface Order {
  id: string
  userUid: string
  email: string
  stripeSessionId: string
  lineItems: OrderLineItem[]
  subtotal: number // in cents
  total: number // in cents
  status: 'paid' | 'refunded' | 'pending'
  fulfillmentStatus: 'pending' | 'shipped' | 'delivered' | 'digital_delivered'
  shippingAddress?: {
    line1: string
    line2?: string
    city: string
    state: string
    postalCode: string
    country: string
  }
  trackingNumber?: string
  trackingCarrier?: string
  downloadUrl?: string // for digital books
  createdAt: string
  updatedAt: string
}

// ─── Auth Functions ───────────────────────────────────────────────

/**
 * Create a new customer account with Firebase Auth + Firestore profile.
 */
export async function signUp(
  email: string,
  password: string,
  displayName: string,
  marketingOptIn = false,
): Promise<{ uid: string; email: string }> {
  const credential = await createUserWithEmailAndPassword(auth, email, password)
  const user = credential.user

  await updateProfile(user, { displayName })

  const profile: CustomerProfile = {
    email: email.trim().toLowerCase(),
    displayName,
    createdAt: new Date().toISOString(),
    marketingOptIn,
  }

  await setDoc(doc(db, 'customers', user.uid), profile)

  // Send verification email (non-blocking — don't fail signup if this errors)
  try {
    await sendEmailVerification(user)
  } catch {
    // ignore — user can request resend later
  }

  return { uid: user.uid, email: profile.email }
}

/**
 * Sign in an existing customer.
 */
export async function signIn(
  email: string,
  password: string,
): Promise<{ uid: string; email: string }> {
  const credential = await signInWithEmailAndPassword(auth, email, password)
  const user = credential.user
  return { uid: user.uid, email: user.email ?? email }
}

/**
 * Sign out the current user.
 */
export async function signOutUser(): Promise<void> {
  await signOut(auth)
}

/**
 * Send a password reset email.
 */
export async function sendPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email)
}

/**
 * Resend verification email to the currently signed-in user.
 */
export async function resendVerificationEmail(): Promise<void> {
  const user = auth.currentUser
  if (user && !user.emailVerified) {
    await sendEmailVerification(user)
  }
}

/**
 * Read a customer profile from Firestore.
 */
export async function getCustomerProfile(
  uid: string,
): Promise<CustomerProfile | null> {
  const snap = await getDoc(doc(db, 'customers', uid))
  return snap.exists() ? (snap.data() as CustomerProfile) : null
}

/**
 * Update a customer profile in Firestore (merges with existing data).
 */
export async function updateCustomerProfile(
  uid: string,
  data: Partial<CustomerProfile>,
): Promise<void> {
  await setDoc(doc(db, 'customers', uid), data, { merge: true })
}

/**
 * Subscribe to a user's auth state changes.
 * Returns the unsubscribe function.
 */
export function subscribeToAuth(
  callback: (user: User | null) => void,
): () => void {
  return onAuthStateChanged(auth, callback)
}

// ─── Order Functions ───────────────────────────────────────────────

/**
 * Subscribe to a customer's orders, sorted newest first.
 * Returns the unsubscribe function.
 */
export function subscribeToOrders(
  uid: string,
  callback: (orders: Order[]) => void,
): () => void {
  const q = query(
    collection(db, 'orders'),
    where('userUid', '==', uid),
    orderBy('createdAt', 'desc'),
  )
  return onSnapshot(
    q,
    (snap) => {
      const orders = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Order[]
      callback(orders)
    },
    (err) => {
      // If the composite index doesn't exist yet, log so dev sees it
      console.error('[Firebase] subscribeToOrders error:', err)
      callback([])
    },
  )
}

/**
 * One-time fetch of a customer's orders (alternative to subscription).
 */
export async function getOrder(orderId: string): Promise<Order | null> {
  const snap = await getDoc(doc(db, 'orders', orderId))
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as Order) : null
}

// ─── Newsletter Functions ───────────────────────────────────────────────

/**
 * Add an email to the newsletter signup list.
 * Uses the lowercased email as the doc ID so re-submissions are idempotent.
 *
 * Stored at: newsletter_signups/{email}
 *
 * IMPORTANT: Your Firestore rules must allow public create on this
 * collection. Add a rule like:
 *
 *   match /newsletter_signups/{email} {
 *     allow create: if request.resource.data.keys().hasOnly(
 *       ['email', 'source', 'createdAt']
 *     );
 *   }
 */
export async function subscribeToNewsletter(
  email: string,
  source: string = 'unknown',
): Promise<void> {
  const cleanEmail = email.trim().toLowerCase()
  if (!cleanEmail || !cleanEmail.includes('@')) {
    throw new Error('Please enter a valid email address.')
  }
  await setDoc(
    doc(db, 'newsletter_signups', cleanEmail),
    {
      email: cleanEmail,
      source,
      createdAt: new Date().toISOString(),
    },
    { merge: true },
  )
}

// ─── Cart Functions ───────────────────────────────────────────────

/**
 * Persisted cart shape stored at `carts/{uid}`. Items are intentionally
 * a minimal serializable list — the React layer rehydrates derived state.
 */
export interface PersistedCartItem {
  productId: string
  name: string
  unitPrice: number // in cents
  quantity: number
  type: 'book' | 'supplement'
  stripePriceId?: string
  lookupKey?: string
  accent?: string
}

export interface PersistedCart {
  items: PersistedCartItem[]
  updatedAt: string
}

/**
 * One-time fetch of a user's cart from Firestore.
 * Returns an empty array if no cart doc exists yet.
 */
export async function getUserCart(uid: string): Promise<PersistedCartItem[]> {
  const snap = await getDoc(doc(db, 'carts', uid))
  if (!snap.exists()) return []
  const data = snap.data() as PersistedCart
  return Array.isArray(data.items) ? data.items : []
}

/**
 * Write the user's cart to Firestore. Replaces any prior cart entirely.
 */
export async function setUserCart(
  uid: string,
  items: PersistedCartItem[],
): Promise<void> {
  await setDoc(doc(db, 'carts', uid), {
    items,
    updatedAt: new Date().toISOString(),
  })
}

/**
 * Real-time subscription to a user's cart.
 * Fires immediately with the current cart, then on every change.
 */
export function subscribeToUserCart(
  uid: string,
  callback: (items: PersistedCartItem[]) => void,
): () => void {
  return onSnapshot(
    doc(db, 'carts', uid),
    (snap) => {
      if (!snap.exists()) {
        callback([])
        return
      }
      const data = snap.data() as PersistedCart
      callback(Array.isArray(data.items) ? data.items : [])
    },
    (err) => {
      console.error('[Firebase] subscribeToUserCart error:', err)
      callback([])
    },
  )
}

/**
 * Clear the user's cart in Firestore (used after a real Stripe checkout success).
 */
export async function clearUserCart(uid: string): Promise<void> {
  await deleteDoc(doc(db, 'carts', uid))
}

export type { User }
